<template>
  <div class="country-manager">
    <h2>Administrador de Países</h2>
    <p>Selecciona los países que deseas mostrar en el selector de códigos telefónicos</p>
    
    <!-- Barra de búsqueda -->
    <div class="search-container">
      <input 
        v-model="searchTerm" 
        type="text" 
        placeholder="Buscar país..." 
        class="search-input"
      />
    </div>
    
    <!-- Acciones rápidas -->
    <div class="actions">
      <button @click="selectAll" class="action-btn">Seleccionar todos</button>
      <button @click="deselectAll" class="action-btn">Deseleccionar todos</button>
      <button @click="saveChanges" class="save-btn">Guardar cambios</button>
    </div>

    <!-- Lista de países con checkboxes -->
    <div class="countries-container">
      <div v-if="loading" class="loading">Cargando países...</div>
      <div v-else-if="filteredCountries.length === 0" class="no-results">
        No se encontraron países que coincidan con la búsqueda
      </div>
      <div 
        v-for="country in filteredCountries" 
        :key="country.dial_code" 
        class="country-item"
      >
        <label class="checkbox-container">
          <input 
            type="checkbox" 
            v-model="country.active" 
            :id="`country-${country.dial_code}`" 
          />
          <span class="checkmark"></span>
        </label>
        <div class="country-info">
          <img 
            v-if="country.flag" 
            :src="country.flag" 
            :alt="`Bandera de ${country.name}`" 
            class="flag-img"
          />
          <span class="country-name">{{ country.name }}</span>
          <span class="country-code">{{ country.dial_code }}</span>
        </div>
      </div>
    </div>
    
    <div v-if="message" :class="['message', messageType]">{{ message }}</div>
    
    <!-- Contador de países seleccionados -->
    <div class="counter">
      {{ selectedCount }} países seleccionados de {{ countryCodes.length }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import axios from "axios";

const store = useStore();
const session = computed(() => store.getters.getUserSession);

const countryCodes = ref([]);
const loading = ref(true);
const searchTerm = ref("");
const message = ref("");
const messageType = ref("success");

// Filtro para países basado en término de búsqueda
const filteredCountries = computed(() => {
  if (!searchTerm.value) return countryCodes.value;
  
  const search = searchTerm.value.toLowerCase();
  return countryCodes.value.filter(country => 
    country.name.toLowerCase().includes(search) || 
    country.dial_code.includes(search)
  );
});

// Contador de países seleccionados
const selectedCount = computed(() => {
  return countryCodes.value.filter(country => country.active).length;
});

// Cargar países con estado activo/inactivo
onMounted(async () => {
  await loadCountryCodes();
});

const loadCountryCodes = async () => {
  loading.value = true;
  const token = session.value ? session.value.token_api : null;

  if (!token) {
    message.value = "No se encontró la sesión del usuario. Vuelve a iniciar sesión.";
    messageType.value = "error";
    loading.value = false;
    return;
  }

  const url = `${process.env.VUE_APP_API_BASE_URL}/country-codes`;
  const options = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token.trim()}`,
    },
  };

  try {
    const response = await axios.get(url, options);
    
    // La API ahora ya nos devuelve la propiedad 'active' para cada país
    countryCodes.value = response.data;
    
    console.log("Países cargados con estado desde la API:", countryCodes.value);
    loading.value = false;
  } catch (err) {
    console.error("Error al cargar códigos de país:", err);
    message.value = "No se pudieron cargar los códigos de país.";
    messageType.value = "error";
    loading.value = false;
  }
};

// Guardar cambios - modifica la función saveChanges
const saveChanges = async () => {
  const activeDialCodes = countryCodes.value
    .filter(country => country.active)
    .map(country => country.dial_code);
  
  const token = session.value ? session.value.token_api : null;
  
  if (!token) {
    showMessage("No se encontró la sesión del usuario", "error");
    return;
  }
  
  try {
    const url = `${process.env.VUE_APP_API_BASE_URL}/save-country-preferences`;
    await axios.post(
      url,
      { active_countries: activeDialCodes },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.trim()}`,
        }
      }
    );

    showMessage("Configuración guardada correctamente", "success");
  } catch (e) {
    console.error("Error al guardar configuración:", e);
    showMessage("Error al guardar la configuración", "error");
  }
};
// Seleccionar todos los países
const selectAll = () => {
  countryCodes.value.forEach(country => {
    country.active = true;
  });
  showMessage("Se han seleccionado todos los países", "success");
};

// Deseleccionar todos los países
const deselectAll = () => {
  countryCodes.value.forEach(country => {
    country.active = false;
  });
  showMessage("Se han deseleccionado todos los países", "success");
};


// Mostrar mensaje temporal
const showMessage = (msg, type = "success") => {
  message.value = msg;
  messageType.value = type;
  
  // Limpiar mensaje después de 3 segundos
  setTimeout(() => {
    message.value = "";
  }, 3000);
};
</script>

<style scoped>
.country-manager {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.search-container {
  margin-bottom: 15px;
}

.search-input {
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}

.actions {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.action-btn {
  padding: 8px 12px;
  background-color: #f0f0f0;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
}

.save-btn {
  padding: 8px 12px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-left: auto;
}

.countries-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 10px;
  max-height: 500px;
  overflow-y: auto;
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
}

.country-item {
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.country-item:hover {
  background-color: #f5f5f5;
}

.country-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-grow: 1;
}

.flag-img {
  width: 24px;
  height: auto;
  border: 1px solid #ddd;
}

.country-name {
  flex-grow: 1;
}

.country-code {
  color: #666;
  font-size: 14px;
}

.checkbox-container {
  position: relative;
  cursor: pointer;
  font-size: 16px;
  user-select: none;
  margin-right: 10px;
  display: flex;
  align-items: center;
}

.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: relative;
  height: 20px;
  width: 20px;
  background-color: #eee;
  border-radius: 3px;
}

.checkbox-container:hover input ~ .checkmark {
  background-color: #ccc;
}

.checkbox-container input:checked ~ .checkmark {
  background-color: #2196F3;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

.checkbox-container .checkmark:after {
  left: 7px;
  top: 3px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
}

.message {
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
}

.success {
  background-color: #dff0d8;
  color: #3c763d;
}

.error {
  background-color: #f2dede;
  color: #a94442;
}

.counter {
  text-align: right;
  color: #666;
  font-size: 14px;
  margin-top: 10px;
}

.loading, .no-results {
  grid-column: 1 / -1;
  padding: 20px;
  text-align: center;
  color: #666;
}
</style>